// Post detail modal — shows every stored metric field for one logged post,
// with edit + delete. Called from Analytics page ("Recent posts" table) and
// All Posts page.
import { useEffect, useState } from "react";
import { X, Edit3, Trash2, Save, Loader2, Youtube, Instagram, Film, Twitter, FileText, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { client, PLATFORMS } from "@/lib/api";

const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";
const SEAL = "var(--seal)";

const PLATFORM_ICONS = {
  youtube_long: Youtube,
  shorts: Film,
  reels: Instagram,
  tweet: Twitter,
  blog: FileText,
};

function fmt(n) {
  if (n == null) return 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function engagementRate(m) {
  const eng = (m.likes || 0) + (m.comments || 0) + (m.shares || 0) + (m.saves || 0);
  if (!m.views) return 0;
  return ((eng / m.views) * 100).toFixed(2);
}

function StatBlock({ label, value, accent }) {
  return (
    <div className="rounded-lg p-3" style={{ background: "var(--bg)", border: "1px solid var(--ink-22)" }}>
      <div className="eyebrow mb-1">{label}</div>
      <div className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500", color: accent || "var(--ink)" }}>
        {value}
      </div>
    </div>
  );
}

export default function PostDetailModal({ postId, onClose, onChanged }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get(`/metrics/${postId}`);
      setPost(r.data);
      setForm(r.data);
    } catch {
      toast.error("Post not found");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!postId) return;
    load();
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      // Coerce number fields
      ["views", "likes", "comments", "shares", "saves", "subscribers_gained"].forEach((k) => {
        body[k] = parseInt(body[k], 10) || 0;
      });
      body.revenue = parseFloat(body.revenue) || 0;
      const r = await client.patch(`/metrics/${postId}`, body);
      setPost(r.data);
      setEditing(false);
      onChanged?.();
      toast.success("Post updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await client.delete(`/metrics/${postId}`);
      toast.success("Post deleted");
      onChanged?.();
      onClose();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!postId) return null;

  const Icon = post ? PLATFORM_ICONS[post.platform] : null;
  const platform = post ? PLATFORMS.find((p) => p.id === post.platform) : null;
  const engRate = post ? engagementRate(post) : 0;

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center"
      onClick={onClose}
      data-testid="post-detail-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-t-2xl md:rounded-2xl w-full md:max-w-2xl max-h-[94vh] overflow-y-auto"
        style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
        data-testid="post-detail-modal"
      >
        <div className="flex items-center justify-between px-5 md:px-6 py-4 sticky top-0 z-10" style={{ background: "var(--surface)", borderBottom: "1px solid var(--ink-22)" }}>
          <div className="flex items-center gap-2.5 min-w-0">
            {Icon && <Icon size={16} style={{ color: SEAL }} />}
            <div className="min-w-0">
              <div className="eyebrow">{platform?.short || post?.platform} · {post?.recorded_for}</div>
              <div className="font-heading text-base md:text-lg truncate" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
                {editing ? "Editing post" : post?.title || "Untitled"}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost flex-shrink-0" data-testid="post-detail-close" style={{ touchAction: "manipulation" }}>
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="animate-spin" size={20} style={{ color: SEAL }} /></div>
        ) : editing ? (
          <div className="p-5 md:p-6 space-y-4">
            {[
              { k: "title", label: "Title" },
              { k: "views", label: "Views", type: "number" },
              { k: "likes", label: "Likes", type: "number" },
              { k: "comments", label: "Comments", type: "number" },
              { k: "shares", label: "Shares", type: "number" },
              { k: "saves", label: "Saves", type: "number" },
              { k: "subscribers_gained", label: "New subscribers", type: "number" },
              { k: "revenue", label: "Revenue (₹)", type: "number", step: "0.01" },
              { k: "notes", label: "Notes", multi: true },
            ].map((f) => (
              <div key={f.k}>
                <label className="eyebrow block mb-1.5">{f.label}</label>
                {f.multi ? (
                  <textarea
                    value={form[f.k] || ""}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="input resize-none"
                    rows={2}
                    data-testid={`post-edit-${f.k}`}
                  />
                ) : (
                  <input
                    type={f.type || "text"}
                    step={f.step}
                    value={form[f.k] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="input"
                    data-testid={`post-edit-${f.k}`}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-5 md:p-6 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatBlock label="Views" value={fmt(post.views)} />
              <StatBlock label="Likes" value={fmt(post.likes)} />
              <StatBlock label="Comments" value={fmt(post.comments)} />
              <StatBlock label="Shares" value={fmt(post.shares)} />
              <StatBlock label="Saves" value={fmt(post.saves)} />
              <StatBlock label="New subs" value={`+${fmt(post.subscribers_gained)}`} accent={SEAL} />
              <StatBlock label="Revenue" value={`₹${(post.revenue || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} accent={SEAL} />
              <StatBlock label="Engagement" value={`${engRate}%`} accent={parseFloat(engRate) >= 5 ? "#89c67e" : "var(--ink)"} />
            </div>
            {post.notes && (
              <div className="rounded-lg p-3" style={{ background: "var(--bg)", border: "1px solid var(--ink-22)" }}>
                <div className="eyebrow mb-1.5">Notes</div>
                <div className="text-sm leading-relaxed" style={{ color: INK_72 }}>{post.notes}</div>
              </div>
            )}
            <div className="rounded-lg p-3 flex items-start gap-3" style={{ background: "rgba(216,168,87,0.06)", border: "1px solid rgba(216,168,87,0.20)" }}>
              <TrendingUp size={14} style={{ color: SEAL, marginTop: 2 }} />
              <div className="text-xs" style={{ color: INK_72 }}>
                {parseFloat(engRate) >= 5
                  ? `Strong engagement — ${engRate}% is above the ~3% platform average. Study what worked.`
                  : parseFloat(engRate) >= 2
                  ? `Healthy engagement (${engRate}%). Try a stronger hook next time to push above 5%.`
                  : `Engagement is low (${engRate}%). Consider re-testing the hook or thumbnail.`}
              </div>
            </div>
          </div>
        )}

        <div
          className="flex flex-col md:flex-row gap-2 px-5 md:px-6 py-4 sticky bottom-0"
          style={{ background: "var(--surface)", borderTop: "1px solid var(--ink-22)", paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        >
          {editing ? (
            <>
              <button onClick={save} disabled={saving} className="btn-primary flex-1 md:flex-initial md:px-5" data-testid="post-detail-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? "Saving…" : "Save changes"}
              </button>
              <button onClick={() => { setEditing(false); setForm(post); }} className="btn-secondary flex-1 md:flex-initial" data-testid="post-detail-cancel-edit">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="btn-primary flex-1 md:flex-initial md:px-5" data-testid="post-detail-edit">
                <Edit3 size={14} /> Edit
              </button>
              <button onClick={remove} className="btn-ghost text-xs md:text-sm md:px-3" style={{ color: "#c97a6a", touchAction: "manipulation" }} data-testid="post-detail-delete">
                <Trash2 size={14} /> Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
